/**
 * ChatInput Component
 * Auto-expanding textarea with image upload and send functionality
 */

import React, { useRef, useEffect } from 'react';
import ImagePreview from './ImagePreview';

/**
 * Send icon
 */
function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"></line>
      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
    </svg>
  );
}

/**
 * Camera/Image icon
 */
function ImageIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  );
}

/**
 * Supported image types
 */
const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

function ChatInput({ 
  value, 
  onChange, 
  onSend, 
  onImageSelect,
  onImageRemove,
  pendingImage,
  disabled = false,
  placeholder = "Ask me anything about the PE320..."
}) {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  }, [value]);

  // Handle key press (Enter to send, Shift+Enter for newline)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && (value.trim() || pendingImage)) {
        onSend();
      }
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      alert(`Unsupported image type. Please use: ${SUPPORTED_IMAGE_TYPES.map(t => t.split('/')[1].toUpperCase()).join(', ')}`);
      return;
    }

    // Validate file size
    if (file.size > MAX_IMAGE_SIZE) {
      alert(`Image too large. Maximum size is ${MAX_IMAGE_SIZE / 1024 / 1024}MB`);
      return;
    }

    // Read file as base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      onImageSelect({
        base64,
        mimetype: file.type,
        name: file.name,
        size: file.size
      });
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle send button click
  const handleSend = () => {
    if (!disabled && (value.trim() || pendingImage)) {
      onSend();
    }
  };

  const canSend = !disabled && (value.trim() || pendingImage);

  return (
    <div className="chatbot-input-area">
      {/* Image preview if one is pending */}
      {pendingImage && (
        <ImagePreview 
          image={pendingImage} 
          onRemove={onImageRemove}
        />
      )}
      
      <div className="chatbot-input-row">
        <div className="chatbot-textarea-wrapper">
          <textarea
            ref={textareaRef}
            className="chatbot-textarea"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
          />
        </div>
        
        <div className="chatbot-input-actions">
          {/* Image upload button */}
          <button
            className="chatbot-input-btn chatbot-upload-btn"
            onClick={handleUploadClick}
            disabled={disabled}
            type="button"
            aria-label="Upload image"
            title="Upload image for analysis"
          >
            <ImageIcon />
          </button>
          
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            className="chatbot-file-input"
            accept={SUPPORTED_IMAGE_TYPES.join(',')}
            onChange={handleFileSelect}
          />
          
          {/* Send button */}
          <button
            className="chatbot-input-btn chatbot-send-btn rf-key-action"
            onClick={handleSend}
            disabled={!canSend}
            type="button"
            aria-label="Send message"
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatInput;
